import type { Ga4Item } from './ga4';

const money = (value: number) => Math.round(value * 100) / 100;

export const buildEventGa4Item = ({
  eventId,
  eventName,
  category,
  index,
}: {
  eventId: string | number;
  eventName: string;
  category?: string;
  index?: number;
}): Ga4Item => ({
  item_id: `event:${eventId}`,
  item_name: eventName,
  item_category: category || 'event',
  ...(index === undefined ? {} : { index }),
});

export const buildTicketGa4Item = ({
  eventName,
  groupId,
  price,
  quantity = 1,
  section,
  index,
}: {
  eventName?: string;
  groupId: string | number;
  price: number;
  quantity?: number;
  section?: string;
  index?: number;
}): Ga4Item => ({
  item_id: `ticket:${groupId}`,
  item_name: eventName ? `${eventName} tickets` : `Ticket group ${groupId}`,
  item_category: 'tickets',
  item_category2: String(groupId),
  ...(section ? { item_variant: section } : {}),
  ...(index === undefined ? {} : { index }),
  price: money(price),
  quantity: Math.max(1, quantity),
});

export const buildHotelGa4Item = ({
  hotelId,
  hotelName,
  price,
  quantity = 1,
  index,
}: {
  hotelId: string | number;
  hotelName: string;
  price: number;
  quantity?: number;
  index?: number;
}): Ga4Item => ({
  item_id: `hotel:${hotelId}`,
  item_name: hotelName,
  item_category: 'hotel',
  item_category2: String(hotelId),
  ...(index === undefined ? {} : { index }),
  price: money(price),
  quantity: Math.max(1, quantity),
});

export const buildFlightGa4Item = ({
  airlineName,
  fareCode,
  price,
  quantity = 1,
  index,
}: {
  airlineName?: string;
  fareCode: string;
  price: number;
  quantity?: number;
  index?: number;
}): Ga4Item => ({
  item_id: `flight:${fareCode}`,
  item_name: `${airlineName || 'Flight'} flight`,
  item_category: 'flight',
  item_category2: fareCode,
  ...(airlineName ? { item_brand: airlineName } : {}),
  ...(index === undefined ? {} : { index }),
  price: money(price),
  quantity: Math.max(1, quantity),
});

export const applyGa4ItemDiscount = (
  items: Ga4Item[],
  discount: number,
  coupon?: string
) => {
  const normalizedDiscount = Math.max(0, money(Number(discount) || 0));
  const subtotal = items.reduce(
    (total, item) =>
      total + Number(item.price || 0) * Math.max(1, Number(item.quantity || 1)),
    0
  );

  if (!normalizedDiscount || !subtotal) {
    return coupon ? items.map((item) => ({ ...item, coupon })) : items;
  }

  return items.map((item) => {
    const quantity = Math.max(1, Number(item.quantity || 1));
    const lineTotal = Number(item.price || 0) * quantity;
    const lineDiscount = Math.min(
      lineTotal,
      normalizedDiscount * (lineTotal / subtotal)
    );

    return {
      ...item,
      ...(coupon ? { coupon } : {}),
      discount: money(lineDiscount / quantity),
    };
  });
};
